import { useMemo } from 'react';
import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { getLpLendingPositionOpenings, getLpSupplyPnl } from '@/shared/graphql/lp/lending';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import TokenAmount from '@/shared/utils/TokenAmount';
import { useLpLendingPositions } from './useLpLendingPositions';

export type PositionEarnedFees = {
  earnedUsd: number;
  earnedAmount: TokenAmount;
};

/**
 * For each asset in the LP's open supply positions:
 *  1. Fetches the most recent position opening timestamps using the isPositionOpening flag
 *  2. Constructs per-asset filters with specific minTimestamp for each asset
 *  3. Fetches PnL snapshots with OR-based filtering to ensure each asset's PnL
 *     is only counted from its current position opening (not previous closed positions)
 */
export const useLpPositionEarnedFees = (idSs58: string, endDate: Date) => {
  const { positionAssets: assets } = useLpLendingPositions({
    idSs58,
  });

  const { data: positionOpeningsData, isLoading: positionOpeningsLoading } = useGqlQuery(
    getLpLendingPositionOpenings,
    {
      variables: { idSs58, assets },
      refetchInterval: 30_000,
      context: { clientName: 'lpProcessor' },
      enabled: assets.length > 0,
    },
  );

  const positionOpenTimestamps = useMemo(() => {
    const result = new Map<ChainflipAsset, string>();
    const agg = positionOpeningsData?.account?.balanceChanges.groupedAggregates;
    if (!agg) return result;

    for (const row of agg) {
      const asset = row.keys?.[0] as ChainflipAsset;
      const timestamp = row.max?.timestamp;
      if (asset && timestamp) result.set(asset, timestamp);
    }

    return result;
  }, [positionOpeningsData]);

  // Construct per-asset filter with OR logic:
  // (asset = USDC AND timestamp >= usdcMinTimestamp AND timestamp <= maxTimestamp) OR
  // (asset = USDT AND timestamp >= usdtMinTimestamp AND timestamp <= maxTimestamp) OR ...
  const filters = useMemo(() => {
    if (assets.length === 0 || positionOpenTimestamps.size === 0) return undefined;

    const maxTimestamp = endDate.toISOString();

    return {
      or: assets
        .filter((asset) => positionOpenTimestamps.has(asset))
        .map((asset) => ({
          and: [
            { asset: { equalTo: asset } },
            { timestamp: { greaterThanOrEqualTo: positionOpenTimestamps.get(asset) } },
            { timestamp: { lessThanOrEqualTo: maxTimestamp } },
          ],
        })),
    };
  }, [assets, positionOpenTimestamps, endDate]);

  const { data: pnlData, isLoading: pnlLoading } = useGqlQuery(getLpSupplyPnl, {
    variables: {
      idSs58,
      groupBy: ['ASSET'],
      filters,
    },
    refetchInterval: 30_000,
    context: { clientName: 'lpProcessor' },
    enabled: assets.length > 0 && positionOpenTimestamps.size > 0, // Only fetch PnL if we have assets and their opening timestamps
  });

  const earnedFeesByAsset = useMemo((): Map<ChainflipAsset, PositionEarnedFees> => {
    const result = new Map<ChainflipAsset, PositionEarnedFees>();
    const agg = pnlData?.account?.snapshots.agg;
    if (!agg) return result;

    for (const row of agg) {
      const asset = row.keys?.[0] as ChainflipAsset;
      // eslint-disable-next-line no-continue
      if (!asset) continue;

      const earnedUsd = Number(row.sum?.supplyPnl1HUsd ?? 0);
      const earnedPnl = row.sum?.supplyPnl1H ?? '0';

      result.set(asset, {
        earnedUsd,
        earnedAmount: TokenAmount.fromAsset(earnedPnl, asset),
      });
    }

    return result;
  }, [pnlData]);

  return {
    earnedFeesByAsset,
    positionOpenTimestamps,
    isLoading: positionOpeningsLoading || pnlLoading,
  };
};
